import { Alert } from 'react-bootstrap'
import * as H from 'history'
import  React from 'react'
import { ClaimController } from '../../../../controllers/ClaimController'
import { ClaimModel } from '../../../../models/ClaimModel'
import { MarketingVisitEntry } from '../../../../models/MarketingVisitEntry'
import { ISupportSpinner } from '../../../../shared/Ajax'
import { Backdrop } from '../../../../shared/Backdrop'
import { HelpForText } from '../../../../shared/fields/HelpForText'
import { Option } from '../../../../shared/fields/Options'
import { Icon } from '../../../../shared/Icon'
import { Table } from '../../../../shared/table'
import { SaveCancelButtons } from '../../controls/SaveCancelButtons'
import { ScheduleInstructions } from '../../controls/ScheduleInstructions'
import {
  calculateMarketingVisitTotal, marketingVisitEntryColumns, MarketingVisitEntryDetails, sortMarketingVisits, copyFromParent,
} from './MarketingVisitEntryDetails'
import { ScheduleTypes } from '../../../../shared/Utility'

export interface MarketingVisitPageProps {
    history: H.History
    model: ClaimModel
    markets: Option<string>[]
    onChange: { (changes: Partial<ClaimModel>, callback?: { (): any }): any }
}

export interface MarketingVisitPageState {
    loading: boolean
    marketingVisitEntries: MarketingVisitEntry[]
}

export class MarketingVisitPage extends React.Component<MarketingVisitPageProps, MarketingVisitPageState> implements ISupportSpinner {
    ajax: ClaimController
    table: Table<MarketingVisitEntry>

    constructor(props: MarketingVisitPageProps) {
      super(props)

      this.ajax = new ClaimController()

      this.state = {
        loading: false,
        marketingVisitEntries: props.model.marketingVisitEntries ? props.model.marketingVisitEntries.slice() : [],
      }
    }

    componentWillUnmount() {
      this.ajax.cancel()
    }

    componentDidUpdate(prevProps: MarketingVisitPageProps, prevState: MarketingVisitPageState) {
      // Delete/update all child trips if primary deleted/updated
      let newEntries: MarketingVisitEntry[] = null
      for (const mv of this.state.marketingVisitEntries) {
        if (mv.primary) {
          const prev = prevState.marketingVisitEntries.filter((r) => r.id == mv.id)
          if (prev.length > 0) {
            if (JSON.stringify(prev[0]) != JSON.stringify(mv)) {
              if (newEntries == null) {
                newEntries = [...this.state.marketingVisitEntries]
              }

              const toReplace = newEntries.filter((r) => r.tripNo == mv.tripNo && !r.primary)
              for (const r of toReplace) {
                const childTrip = { ...r }
                copyFromParent(childTrip, mv)
                newEntries.splice(newEntries.indexOf(r), 1, childTrip)
              }
            }
          }
        }
      }

      const removed = prevState.marketingVisitEntries.filter((r) => !this.state.marketingVisitEntries.some((x) => x.id == r.id) && r.primary)
      if (removed.length > 0) {
        if (newEntries == null) {
          newEntries = [...this.state.marketingVisitEntries]
        }

        for (const rem of removed) {
          const toRemove = newEntries.filter((x) => rem.tripNo == x.tripNo)

          for (const mv of toRemove) {
            newEntries.splice(newEntries.indexOf(mv), 1)
          }
        }
      }

      if (newEntries != null) {
        this.setState({
          marketingVisitEntries: newEntries,
        })
      }
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    save = () => {
      this.table.saveAndClose((res) => {
        if (res) {
          if (this.state.marketingVisitEntries.filter((r) => r.invalid).length == 0) {
            this.props.onChange({ marketingVisitEntries: this.state.marketingVisitEntries }, () => {
              this.ajax.updateSchedules(this, this.props.model, ScheduleTypes.MarketingVisits, () => {
                this.props.history.goBack()
              })
            })
          } else {
            alert('Please correct records marked with the warning sign.')
          }
        }
      })
    }

    render() {
      const columns = [...marketingVisitEntryColumns(this.props.markets)]

      const invalidRows = this.state.marketingVisitEntries.filter((r) => r.invalid).length > 0
      if (invalidRows) {
        columns.splice(0, 0, { name: '', content: (d) => (d.invalid ? <Icon type="warning" /> : <span />) })
      }

      const sortedMarketingVisits = this.state.marketingVisitEntries.slice()
      sortedMarketingVisits.sort((a, b) => sortMarketingVisits(a, b))

      return (
        <>
          <SaveCancelButtons onCancel={this.props.history.goBack} onSave={this.save} />

          <ScheduleInstructions
            title="Marketing visits"
            helpLink="https://www.austrade.gov.au/ArticleDocuments/6917/Guide-to-Schedule-2.pdf.aspx"
          />

          <div className="mt-3 mb-3">
            <Alert className="emdg-page-alerts">
              Do not claim accommodation/meal expenses, an allowance of $350 per day will be added automatically in your first line of expenses.
              <br />
              What you can claim:
              <ul>
                <li>
                  Airfare including overseas visit allowance (OVA)
                  <HelpForText id="airfareAndOVA-help" message="ticket costs (only 65% of first class airfares are allowed). The eligible working days is automatically populated and you will need to reduce this for ineligible activities such as non marketing days and domestic travel." />
                </li>
                <li>
                  Air ticket costs – other
                  <HelpForText id="airfareOther-help" message="expenses related to the flight such as baggage costs, airline food and drinks, seat allocations, upgrades, inflight entertainment, carbon offsets." />
                </li>
                <li>
                  Overseas Visit Allowance (OVA) only
                  <HelpForText id="ovaOnly-help" message="this is used when the airfare has been paid for outside the grant year." />
                </li>
              </ul>
            </Alert>
            <Alert className="emdg-page-alerts">
              <p>
                Applications lodged in the 2021-22 financial year can claim expenses for events and trips that have been cancelled due to COVID-19 circumstances, if they have not been refunded.
              </p>
              <p>
                <b>However, refunded expenses are not claimable.</b>
              </p>
              <p>
                Credit issued – this expenditure is allowed when the expenditure was incurred as long as the applicant can demonstrate the intention of the expenditure was for an approved promotional purpose. The credit should then be used to pay for promotional expenditure before its expiry and would not be claimable at the time of use as it has already been reimbursed when incurred.
              </p>
              <p>
                Non-Refunds – claimable however the applicant must demonstrate the expense was for an approved promotional purpose.
              </p>
              <p>
                If you did travel overseas and are claiming the OVA you will be required to upload your "Travel Restriction Exemption" document. Please upload this in "Other documents" in the Supporting documents section of the EMDG Application form.
              </p>
            </Alert>
          </div>

          <Table<MarketingVisitEntry>
            id="table"
            label="Schedule 2: Marketing visits"
            ref={(r) => this.table = r}
            value={sortedMarketingVisits}
            onChange={(marketingVisitEntries) => this.setState({ marketingVisitEntries })}
            columns={columns}
            renderSection={(p, i) => <MarketingVisitEntryDetails {...p} markets={this.props.markets} claim={this.props.model} />}
            importUrl="/api/marketingvisit/import"
            exportUrl="/api/marketingvisit/export"
            importContext={{ claimYear: this.props.model.claimYear, claimTwoYears: this.props.model.claimTwoYears }}
            match={(a, b) => a.id == b.id}
            total={calculateMarketingVisitTotal}
            allowEdit
            allowImport
            invalidRows={invalidRows}
          />

          <SaveCancelButtons onCancel={this.props.history.goBack} onSave={this.save} />

          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
